import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm, useWatch} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Stack from "@mui/material/Stack";
import React from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {SearchData} from "@/widget/SearchData/SearchData";
import {WriteChanceForm} from "@/pages/Chance/WriteChance";
import {InputFile, validationFileSchema} from "@/widget/UploadFile/InputFile";
import DatePicker from "react-datepicker";
import {DatePickers} from "@/theme/DatePickers";
import {NumbersOutlined} from '@mui/icons-material';
import ProductForm from "@/pages/Contract/ProductForm";
import {useCreateContract, useQueryContract, useUpdateContract} from "@/service/contract.s";
import {CustomerPO} from "@/model/customer";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {TagOut} from "@/widget/TagOut/TagOut";
import PaybackForm from "@/pages/Contract/PaybackForm";
import {checkQueryKey, emptyValue, filterDicts, getSelectValues, iso2Date, setValues} from "@/helper/method";
import {ProductPO} from "@/model/product";
import isEmpty from "lodash/isEmpty";
import {isNumber} from "lodash";
import {PaybackPO} from "@/model/finance";
import {SearchContactVO} from "@/service/contact.s";

const validationProductSchema = z.object({
    price: z.unknown(),
    num: z.unknown({message: "请填写数量"}),
    chanceId: z.unknown(),
    chanceName: z.unknown(),
    productIds: z.string({message: "请选择服务类型"}).nonempty({message: "请选择服务类型"}),
    productNames: z.unknown(),
    productType: z.unknown(),
    productTypeId: z.unknown(),
    categoryId: z.unknown(),
    retardId: z.unknown(),
    retard: z.unknown(),
    insideDeadline: z.unknown(),
    category: z.unknown(),
    // category: z.string({message: "请选择服务名称"}).nonempty({message: "请选择服务名称"}),
    kindId: z.unknown(),
    kind: z.unknown(),
    id: z.unknown()
}).superRefine((values, ctx) => {
    if (values.productTypeId != 5) {
        if (isEmpty(values?.price)) {
            ctx.addIssue(
                {code: z.ZodIssueCode.custom, message: "请填写价格", path: ['price']}
            )
        }
        if (!isNumber(values?.num)) {
            ctx.addIssue(
                {code: z.ZodIssueCode.custom, message: "请填数量", path: ['num']}
            )
        }
        if (values.productTypeId == 1) {
            if (!isNumber(values?.retardId)) {
                ctx.addIssue(
                    {code: z.ZodIssueCode.custom, message: "请选择减缓比例", path: ['retardId']}
                )
            }
        }
    }
})

const validationPaybackSchema = z.object({
    id: z.unknown().default(0),
    remark: z.ostring(),
    productId: z.string().optional(),
    flag: z.number({message: "请选择是否回款"}).default(1),
    productName: z.string({message: "请填写产品名称"}).nonempty({message: "请填写产品名称"}),
    paymentMoney: z.string({message: '请填写回款金额'}).nonempty({message: "请填写回款金额"}),
    paymentDate: z.date({message: "请选择回款时间"}),
    receivingMethod: z.string({message: "请选择到账方式"}),
    paymentMethod: z.string({message: "请选择回款方式"}),
    receivingMethodId: z.unknown(),
    paymentMethodId: z.unknown(),
});

const validationContractSchema = z.object({
    id: z.number().default(0),
    instanceId: z.number().default(0),
    executeId: z.unknown(),
    customerId: z.number({message: "请选择签约客户"}),
    customerName: z.string().optional(),
    companyIds: z.string({message: "请选择签约公司"}),
    companyNames: z.string().optional(),
    tempCompanies: z.array(z.object({label: z.string(), value: z.number()})),
    paybackPlanIds: z.string({message: "请选择回款计划"}).nonempty({message: "请选择回款计划"}),
    paybackPlans: z.string().optional(),
    tempPaybackPlans: z.array(z.object({label: z.string(), value: z.number()})),
    contactId: z.number({message: "请选择对接人"}),
    contact: z.string().optional(),

    dealTime: z.date({message: "请选择成交日期"}),
    contractNum: z.string({message: "请填写合同编号"}).nonempty({message: "请填写合同编号"}),
    remark: z.string().optional(),
    originals: z.array(validationFileSchema).optional(),
    annexes: z.array(validationFileSchema).optional(),
    products: z.array(validationProductSchema),
    paybacks: z.array(validationPaybackSchema).optional(),
})

export type WriteContractForm = z.infer<typeof validationContractSchema>
export type WriteProductForm = z.infer<typeof validationProductSchema>
export type WritePaybackForm = z.infer<typeof validationPaybackSchema>

const defaultValues = {
    ...emptyValue(validationContractSchema),
    customerName: "",
    originals: [],
    annexes: [],
    products: [{}],
    paybacks: []
}

export const WriteContract = () => {
    const {paybackPlan, company} = useAllDict()
    const {setToast} = useGlobalActions()
    const {mutateAsync: createContractMutate, isPending: createContractPending} = useCreateContract()
    const {mutateAsync: updateContractMutate, isPending: updateContractPending} = useUpdateContract()

    const {onSearch, id: drawerId, write, execute} = useDrawerModal()
    const {data: contractData} = useQueryContract({id: drawerId!}, checkQueryKey(drawerId))
    const contract = contractData?.data
    const {setDrawerModal} = useGlobalActions()
    const initValues = () => {
        if (write == 'edit') {
            const products = contract?.products?.map((item: ProductPO) => ({
                ...item,
                chanceName: item?.chanceName,
                chanceId: item?.chanceId,
                productNames: `${item?.productType},${item?.kind}`,
                productIds: `${item?.productTypeId},${item?.kindId}`,
                insideDeadline: iso2Date(item?.insideDeadline)
            }))
            const paybacks = contract?.paybacks?.map((item: PaybackPO) => ({
                ...item,
                paymentDate: iso2Date(item?.paymentDate)
            }))
            return {
                ...contract, dealTime: iso2Date(contract?.dealTime), tempCompanies: filterDicts(company, contract?.companyIds),
                tempPaybackPlans: filterDicts(paybackPlan, contract?.paybackPlanIds), products, paybacks
            }
        }
        return {}
    }

    const {
        register,
        control,
        setValue,
        reset,
        handleSubmit,
        formState: {errors,},
    } = useForm<WriteContractForm>({
        resolver: zodResolver(validationContractSchema),
        values: initValues() as WriteContractForm,
        defaultValues: defaultValues,
    });

    const onSubmit = async (values: WriteContractForm) => {
        const result = write == 'add' ? await createContractMutate(values) : await updateContractMutate({...values, ...execute, id: drawerId!})
        if (result.code == 0) {
            setDrawerModal({open: false})
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
            onSearch && onSearch()
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }
    const customerId: number = useWatch({
        name: 'customerId',
        control
    })

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={1.5} direction={"column"}>
                <Grid container spacing={3}>
                    <Grid size={6}>
                        <SearchData<WriteChanceForm, CustomerPO> errMsg={errors?.customerId?.message} setValue={setValue}
                                                                 control={control} attr={"customerName"} label={"签约客户"}
                                                                 attrId={"customerId"} onSelect={() => {
                            setValue("contactId", 0)
                            setValue("contact", '')
                        }}/>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>签约公司</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => {
                                return <Autocomplete
                                    multiple={true}
                                    value={value || []}
                                    options={company}
                                    disableListWrap={true}
                                    renderValue={(values) => {
                                        return values?.map((value: DictPO, index: number) => <TagOut key={index} index={index} label={value.label}/>)
                                    }}
                                    getOptionLabel={(option: DictPO) => option.label}
                                    getOptionKey={(option: DictPO) => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    onChange={(_, options) => {
                                        const values = getSelectValues(options, {key: "label", val: 'value'}, {key: "companyIds", val: "companyNames"})
                                        setValues(values, setValue as any)
                                        onChange(options)
                                    }}
                                    disableCloseOnSelect={true}
                                    renderInput={(params) => (
                                        <TextField error={!!errors?.companyIds}
                                                   sx={{"& .MuiOutlinedInput-root": {height: 'auto', minHeight: '2.5rem'}}}
                                                   helperText={errors?.companyIds?.message} placeholder={"选择签约公司"}
                                                   {...params} size={'small'}/>
                                    )}
                                />
                            }} name={"tempCompanies"}/>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} autoFocus={true} required={true}>
                            <FormLabel htmlFor="dealTime">成交时间</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                                <DatePicker
                                    showYearDropdown={true}
                                    showMonthDropdown={true}
                                    dateFormat="yyyy/MM/dd"
                                    selected={value}
                                    onChange={date => onChange(date)}
                                    id='account-settings-date'
                                    locale='zhCN'
                                    placeholderText={"选择成交时间"}
                                    customInput={<TextField error={!!errors?.dealTime}
                                                            helperText={errors?.dealTime?.message} sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                    />}
                                />
                            </DatePickers>} name={"dealTime"}/>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} autoFocus={true} required={true}>
                            <FormLabel htmlFor="contractNum">合同编号</FormLabel>
                            <TextField
                                error={!!errors?.contractNum}
                                helperText={errors?.contractNum?.message}
                                id="contractNum"
                                {...register("contractNum")}
                                slotProps={{
                                    input: {
                                        inputProps: {maxLength: 40},
                                        endAdornment: <NumbersOutlined color="disabled"/>,
                                    },
                                }}
                                placeholder="填写合同编号"
                                variant="outlined"
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>回款计划</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => {
                                return <Autocomplete
                                    multiple={true}
                                    value={value || []}
                                    slotProps={{chip: {size: 'small',}}}
                                    options={paybackPlan}
                                    disableListWrap={true}
                                    renderValue={(values) => {
                                        return values?.map((value: DictPO, index: number) => <TagOut key={index} index={index} label={value.label}/>)
                                    }}
                                    onChange={(_, options) => {
                                        const values = getSelectValues(options, {key: "label", val: 'value'}, {key: "paybackPlanIds", val: "paybackPlans"})
                                        setValues(values, setValue as any)
                                        onChange(options)
                                    }}
                                    disableCloseOnSelect={true}
                                    getOptionLabel={(option: DictPO) => option.label}
                                    getOptionKey={(option: DictPO) => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    renderInput={(params) => (
                                        <TextField error={!!errors?.paybackPlanIds}
                                                   sx={{"& .MuiOutlinedInput-root": {height: 'auto', minHeight: '2.5rem'}}}
                                                   helperText={errors?.paybackPlanIds?.message} placeholder={"选择回款计划"} {...params} size={'small'}/>
                                    )}
                                />
                            }} name={"tempPaybackPlans"}/>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <SearchData<WriteChanceForm, SearchContactVO> readOnly={!checkQueryKey(customerId)} query={{customerId}} errMsg={errors?.contactId?.message}
                                                                      setValue={setValue} autoLoad={true} tag={10}
                                                                      control={control} attr={"contact"} label={"对接人"}
                                                                      attrId={"contactId"}/>
                    </Grid>
                    <Grid size={12}>
                        <FormControl fullWidth={true}>
                            <FormLabel htmlFor="remark">备注</FormLabel>
                            <TextField
                                error={!!errors?.remark}
                                helperText={errors?.remark?.message}
                                id="remark"
                                multiline
                                rows={3}
                                {...register("remark")}
                                slotProps={{
                                    input: {
                                        sx: {height: 80},
                                        inputProps: {maxLength: 250},
                                    },
                                }}
                                placeholder="填写备注"
                                fullWidth
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <InputFile<WriteContractForm> tag={11} mode={'multiple'} isSave={true} targetId={drawerId} label={'合同原件'} attr={register("originals")} control={control}
                                                      errMsg={errors?.originals?.[0]?.name?.message}/>
                    </Grid>
                    <Grid size={6}>
                        <InputFile<WriteContractForm> mode={'multiple'} tag={1} isSave={true} targetId={drawerId} label={'其他附件'} attr={register("annexes")} control={control}
                                                      errMsg={errors?.annexes?.[0]?.name?.message}/>
                    </Grid>
                </Grid>
                <ProductForm<WriteContractForm> setValue={setValue} control={control} errors={errors} register={register}/>
                <PaybackForm<WriteContractForm> setValue={setValue} control={control} errors={errors} register={register}/>
                <DrawerFooter loading={createContractPending || updateContractPending} onReset={() => {
                    reset(defaultValues)
                }}/>
            </Stack>
        </Box>
    );
};
